import { call, ForkEffect, put, select, takeLatest } from "redux-saga/effects";
import {
  paymentActions,
  addMerchantDetailsSuccessAction,
  addMerchantDetailsFailureAction,
  getMerchantDetailsSuccessAction,
  getMerchantDetailsByIdSuccessAction,
  submitPaymentSuccessAction,
  submitPaymentFailureAction,
  getMerchantDetailsByIdFailureAction,
  getMerchantDetailsFailureAction,
  fetchPlanDataSuccess,
  fetchPlanDataFailure,
} from "./actions";
import {
  IAddMerchantDetailsAction,
  IFetchPaymentDataAction,
  IFetchPaymentPlanPayload,
  IGetMerchantDetailsAction,
  IGetMerchantDetailsByIdAction,
  IMerchantDetails,
  INTTDetailsPayload,
  IPaymentInit,
  IPaymentInitiationResponse,
  IPayuPayload,
  IPlansDetails,
  IRefundAmountAction,
  IRefundResponse,
  ISubmitPaymentAction,
  ISubmitPaymentPayload,
  IVerifySubscriptionAction,
  IVerifySubscriptionPayload,
} from "./interface";
import { PaymentRepository } from "@common/domain/repositories/paymentRepository";
import { NavigationUrl, URLs } from "@navigator/NavigationUrl";
import Navigator from "@navigator/index";
import { getConfig } from "@config/index";
import { getAuthSessionTokenSelector } from "../auth/selectors";
import { getActiveFormDataSelector } from "../formBuilder/selectors";
import { IForm } from "@common/redux/formBuilder/interface";

function* addMerchantDetails(action: IAddMerchantDetailsAction) {
  try {
    const merchantDetails: IMerchantDetails = action.payload.merchantDetails;
    const onAddMerchantDetailsSuccess =
      action.payload.onAddMerchantDetailsSuccess;
    const response: IMerchantDetails = yield call(
      PaymentRepository.addMerchantDetails,
      merchantDetails
    );
    yield put(addMerchantDetailsSuccessAction(response));
    onAddMerchantDetailsSuccess(response?.paymentId);
  } catch (e) {
    yield put(addMerchantDetailsFailureAction(e.message));
  }
}

function* fetchPaymentPlans(action: IFetchPaymentDataAction) {
  try {
    const response: IPlansDetails = yield call(
      PaymentRepository.fetchPaymentPlans,
      action
    );
    yield put(fetchPlanDataSuccess(response));
  } catch (e) {
    yield put(fetchPlanDataFailure(e.message));
  }
}

function* paymentInitiationSaga(action: IPaymentInit) {
  const failureAction = action?.payload?.onFailure;
  const planId = action?.payload?.planId ?? "";
  const planLabel = action?.payload?.planLabel ?? "";
  try {
    const successAction = action?.payload?.onSuccess;
    const response: IPaymentInitiationResponse = yield call(
      PaymentRepository.paymentInitiation,
      action?.payload
    );
    yield successAction(response, planId,planLabel );
  } catch (e) {
    yield failureAction(e.message);
  }
}

function* refundAmountSaga(action: IRefundAmountAction) {
  const { onSuccess, onError } = action.payload;
  try {
    const response: IRefundResponse = yield call(
      PaymentRepository.refundAmount,
      action.payload
    );
    onSuccess(response?.description ?? "");
  } catch (e) {
    onError();
  }
}

function* verifySubscription(action: IVerifySubscriptionAction) {
  const { subscriptionId, onSuccess, onError } = action.payload;
  try {
    const response: any = yield call(
      PaymentRepository.getSubscriptionStatus,
      subscriptionId
    );
    onSuccess(response ?? {});
  } catch (e) {
    onError();
  }
}
function* getMerchantDetails(action: IGetMerchantDetailsAction) {
  try {
    const userId: string = action.payload.userId;
    const response: IMerchantDetails[] = yield call(
      PaymentRepository.getMerchantDetails,
      userId
    );
    yield put(getMerchantDetailsSuccessAction(response));
  } catch (e) {
    yield put(getMerchantDetailsFailureAction(e.message));
  }
}

function* submitPayment(action: ISubmitPaymentAction) {
  try {
    const paymentData: ISubmitPaymentPayload = action.payload.paymentData;
    const paymentMode: string = action.payload.paymentMode;
    const submitPaymentPayload = {
      paymentData,
      paymentMode,
    };
    if (paymentMode.toLowerCase() == "nttdata") {
      try {
        const token: string = yield select(getAuthSessionTokenSelector);
        const payloadNttData: INTTDetailsPayload = {
          email: paymentData.email,
          submissionId: paymentData.submissionId,
          formId: paymentData.formId,
          mobileNo: paymentData.mobileNo,
          txnAmt: paymentData.txnAmt,
          firstName: paymentData.firstname,
          paymentId: paymentData.paymentId,
          productinfo: paymentData.productinfo,
          token: `Bearer ${token}`,
        };
        const url =
          getConfig().APP_BE_BASE_URL +
          "api/payment-service/public/payment/nttdata/submitPaymentDetail";
        yield initiateToPaymentPage(url, "post", payloadNttData);
        yield put(submitPaymentSuccessAction({}));
      } catch (e) {
        yield put(submitPaymentFailureAction(e.message));
        Navigator.push(NavigationUrl.generate(URLs.paymentFailure));
      }
    } else {
      const response = yield call(
        PaymentRepository.submitPayment,
        submitPaymentPayload
      );
      yield put(submitPaymentSuccessAction(response));
      if (response?.PAYTM_URL) {
        redirectToPaymentPage(response?.PAYTM_URL, "post", response);
      } else if (response?.razorpay_URL) {
        redirectToURL(response?.razorpay_URL);
      } else if (paymentMode === "payu") {
        const payload: IPayuPayload = {
          amount: paymentData.txnAmt,
          email: paymentData.email,
          firstname: paymentData.firstname,
          hash: response.hash,
          key: response.key,
          furl:
            getConfig().APP_BE_BASE_URL + "api/payment-service/public/payment/payu/pgresponse",
          // phone: paymentData.mobileNo,
          productinfo: paymentData.productinfo,
          txnid: response.txnid,
          surl:
            getConfig().APP_BE_BASE_URL + "api/payment-service/public/payment/payu/pgresponse",
        };
        redirectToPaymentPage(
          getConfig().PAYU_PAYMENT_URL + "/_payment",
          "post",
          payload
        );
      } else {
        Navigator.push(NavigationUrl.generate(URLs.paymentFailure));
      }
    }
  } catch (e) {
    yield put(submitPaymentFailureAction(e.message));
    Navigator.push(NavigationUrl.generate(URLs.paymentFailure));
  }
}

function* getMerchantDetailsById(action: IGetMerchantDetailsByIdAction) {
  try {
    const formData: IForm = yield select(getActiveFormDataSelector);
    const isAnonymousSubAllowed: boolean = formData.isAnonymousSubAllowed;
    const paymentIds: string[] = action.payload.paymentIds;
    const paymentIdsString = paymentIds.toString();
    const response: IMerchantDetails[] = yield call(
      PaymentRepository.getMerchantDetailsById,
      paymentIdsString,
      isAnonymousSubAllowed
    );
    yield put(getMerchantDetailsByIdSuccessAction(response));
  } catch (e) {
    yield put(getMerchantDetailsByIdFailureAction(e.message));
  }
}

export function* watchPayment(): Generator<ForkEffect> {
  yield takeLatest(paymentActions.PATMENT_INITIATION, paymentInitiationSaga);
  yield takeLatest(paymentActions.ADD_MERCHANT_DETAILS, addMerchantDetails);
  yield takeLatest(paymentActions.FETCH_PLANS_DATA, fetchPaymentPlans);
  yield takeLatest(paymentActions.REFUND_AMOUNT, refundAmountSaga);
  yield takeLatest(paymentActions.VERIFY_SUBSCRIPTION, verifySubscription);
  yield takeLatest(paymentActions.GET_MERCHANT_DETAILS, getMerchantDetails);
  yield takeLatest(paymentActions.SUBMIT_PAYMENT, submitPayment);
  yield takeLatest(
    paymentActions.GET_MERCHANT_DETAILS_BY_IDS,
    getMerchantDetailsById
  );
}

const redirectToPaymentPage = (url, method, paymentData) => {
  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("style", "display:none");
  form.setAttribute("action", url);
  for (const key in paymentData) {
    if (paymentData.hasOwnProperty(key)) {
      const hiddenField = document.createElement("input");
      hiddenField.setAttribute("type", "hidden");
      hiddenField.setAttribute("name", key);
      hiddenField.setAttribute("value", paymentData[key]);
      form.appendChild(hiddenField);
    }
  }
  document.body.appendChild(form);
  form.submit();
};

const redirectToURL = (url) => {
  window.open(url, "_self");
};

const initiateToPaymentPage = (url, method, paymentData) => {
  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("style", "display:none");
  form.setAttribute("action", url);
  // form.setAttribute("Authorization",  `Bearer ${paymentData.token}`);

  // form.setAttribute("enctype", "multipart/form-data");
  for (const key in paymentData) {
    if (paymentData.hasOwnProperty(key)) {
      const hiddenField = document.createElement("input");
      hiddenField.setAttribute("type", "hidden");
      hiddenField.setAttribute("name", key);
      hiddenField.setAttribute("value", paymentData[key]);
      form.appendChild(hiddenField);
    }
  }
  document.body.appendChild(form);
  form.submit();
};
